import React, { RefObject } from 'react';
import { Bot, User, Copy, Check, Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { Message } from './types';

interface AIChatMessageListProps {
    messages: Message[];
    isLoading: boolean;
    messagesEndRef: RefObject<HTMLDivElement>;
    isDark: boolean;
    copiedId: string | null;
    copyToClipboard: (content: string, id: string) => void;
}

export const AIChatMessageList: React.FC<AIChatMessageListProps> = ({
    messages,
    isLoading,
    messagesEndRef,
    isDark,
    copiedId,
    copyToClipboard,
}) => {
    return (
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
                <div
                    key={msg.id}
                    className={clsx(
                        'flex gap-3',
                        msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                    )}
                >
                    {/* Avatar */}
                    <div className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                        msg.role === 'ai'
                            ? 'bg-gradient-to-br from-purple-500 to-blue-600'
                            : 'bg-blue-500'
                    )}>
                        {msg.role === 'ai' ? (
                            <Bot className="w-5 h-5 text-white" />
                        ) : (
                            <User className="w-5 h-5 text-white" />
                        )}
                    </div>

                    {/* Message Content */}
                    <div className={clsx(
                        'flex-1 max-w-[80%] rounded-lg p-3',
                        msg.role === 'user'
                            ? (isDark ? 'bg-blue-600' : 'bg-blue-500') + ' text-white'
                            : isDark ? 'bg-slate-700' : 'bg-slate-100'
                    )}>
                        <div className={clsx(
                            'text-sm whitespace-pre-wrap',
                            msg.role === 'ai' && (isDark ? 'text-slate-100' : 'text-slate-800')
                        )}>
                            {msg.content.split(/(\*\*.*?\*\*)/g).map((part, i) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                    return <strong key={i} className="text-purple-400">{part.slice(2, -2)}</strong>;
                                }
                                return <span key={i}>{part}</span>;
                            })}
                        </div>
                        {msg.role === 'ai' && (
                            <button
                                onClick={() => copyToClipboard(msg.content, msg.id)}
                                className={clsx(
                                    'mt-2 flex items-center gap-1 text-xs transition-colors',
                                    isDark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700'
                                )}
                            >
                                {copiedId === msg.id ? (
                                    <>
                                        <Check className="w-3 h-3" />
                                        Copied!
                                    </>
                                ) : (
                                    <>
                                        <Copy className="w-3 h-3" />
                                        Copy
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            ))}
            {isLoading && (
                <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                    </div>
                    <div className={clsx(
                        'rounded-lg p-3',
                        isDark ? 'bg-slate-700' : 'bg-slate-100'
                    )}>
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-500 animate-spin" />
                            <span className={clsx('text-sm', isDark ? 'text-slate-300' : 'text-slate-600')}>
                                Thinking...
                            </span>
                        </div>
                    </div>
                </div>
            )}
            <div ref={messagesEndRef} />
        </div>
    );
};
